import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSession } from '../../auth/useSession.js';
import { creerSaisie, lireSaisies, modifierSaisie, supprimerSaisie } from '../../lib/api.js';
import type { CorpsSaisie, RessourceSaisie, SaisiesParRessource } from '../../types.js';
import { CLE_COMPTABILITE } from '../Comptabilite/useComptabilite.js';

/**
 * Une ressource saisie : sa liste et ses trois écritures (ARTO-58). La clé de
 * requête porte l'id du compte (comme la vue d'aigle) : la liste en cache d'un
 * autre compte n'est jamais servie. Toute écriture relit la liste, la vue
 * d'aigle ET la comptabilité, dont les totaux en dépendent.
 */
export function useSaisies<R extends RessourceSaisie>(ressource: R) {
  const { moi } = useSession();
  const compteId = moi?.compte.id ?? null;
  const client = useQueryClient();

  const liste = useQuery({
    queryKey: ['console', 'saisies', compteId, ressource],
    queryFn: () => lireSaisies(ressource),
  });

  const relire = async (): Promise<void> => {
    await Promise.all([
      client.invalidateQueries({ queryKey: ['console', 'saisies', compteId, ressource] }),
      client.invalidateQueries({ queryKey: ['console', 'vue-d-aigle'] }),
      // Un abonnement ou un revenu change les recettes d'un mois de la comptabilité.
      client.invalidateQueries({ queryKey: CLE_COMPTABILITE }),
    ]);
  };

  const creer = useMutation({
    mutationFn: (corps: CorpsSaisie) => creerSaisie(ressource, corps),
    onSuccess: relire,
  });
  const modifier = useMutation({
    mutationFn: ({ id, corps }: { id: string; corps: CorpsSaisie }) =>
      modifierSaisie(ressource, id, corps),
    onSuccess: relire,
  });
  const supprimer = useMutation({
    mutationFn: (id: string) => supprimerSaisie(ressource, id),
    onSuccess: relire,
  });

  return {
    lignes: (liste.data ?? []) as SaisiesParRessource[R][],
    chargement: liste.isLoading,
    enErreur: liste.isError,
    creer: (corps: CorpsSaisie) => creer.mutateAsync(corps),
    modifier: (id: string, corps: CorpsSaisie) => modifier.mutateAsync({ id, corps }),
    supprimer: (id: string) => supprimer.mutateAsync(id),
  };
}
